"use client";
import { motion } from "framer-motion";
import { useLayoutEffect, useRef } from "react";
import Image from "next/image";
import { withBasePath, withMediaUrl } from "../../../lib/basePath";
import { cn } from "@/app/lib/utils/cn";
import { fadeUp, stagger } from "@/app/lib/utils/motionVariants";


type Testimonial = {
  name: string;
  company: string;
  review: string;
  image?: {
    url: string;
    alternativeText?: string;
  };
};

type TestimonialsProps = {
  data?: {
    title?: string;
    titleHighlight?: string;
    testimonials?: Testimonial[];
  };
  cardClassName?: string;
  itemClassName?: string;
  disableSlider?: boolean;
};


// const testimonialsData = [
//     {
//         name: 'Raxit Bhai Dholariya',
//         company: 'Patel Construction',
//         photo: 'raxit-dholariya.webp',
//         quote: "Plotter has revolutionized our showcasing process. It's sleek, smart, and gives us a competitive edge in a crowded market.",
//     },
//     {
//         name: 'Paresh Patel',
//         company: 'Sahjanand Buildcon',
//         photo: 'paresh-patel.webp',
//         quote: "Since implementing Plotter, our property tours have become smoother and more engaging. The user experience is flawless, and feedback has been overwhelmingly positive.",
//     },
//     {
//         name: 'Babu Bhai Dangar',
//         company: 'Dwarkesh Developers',
//         photo: 'babu-dangar.webp',
//         quote: "The Plotter solution has completely transformed our property showcasing experience. Navigation is easy, and customers love it!",
//     },
//     {
//         name: 'Sagar Bhai Chovtiya',
//         company: 'Tridev Builders',
//         photo: 'sagar-chovtiya.webp',
//         quote: "The Plotter solution has elevated the way we present our listings. It's intuitive, visually impressive, and our clients are genuinely impressed!",
//     },
// ];

interface TestimonialCardProps {
    quote: string;
    name: string;
    company: string;
    photo?: string;
    alternativeText?: string;
    className?: string;
}

const TestimonialCard = ({ quote, name, company, photo, alternativeText, className }: TestimonialCardProps) => (
    <div className={cn("relative flex flex-col justify-between min-h-[250px] h-full bg-white/[0.04] border border-white/[0.08] rounded-[20px] backdrop-blur-[20px] overflow-hidden", className)}>
        <span className="absolute top-2 left-3 text-[64px] leading-none font-serif bg-gradient-to-br from-[#00D4AA] to-[#4A9EFF] bg-clip-text text-transparent opacity-35 pointer-events-none select-none">&ldquo;</span>
        <p className="flex-1 text-white text-[15px] leading-[1.8] italic lg:px-6 pt-12 lg:pb-6 px-3 pb-4 overflow-hidden">{quote}</p>
        <div className="flex-shrink-0 mt-auto">
            <div className="border-t border-white/10" />
            <div className="flex items-center gap-3 lg:px-6 lg:py-4 px-3 py-3">
                <div className="p-[2px] rounded-full bg-gradient-to-br from-[#00D4AA] to-[#4A9EFF] flex-shrink-0">
                    {photo ? (
                        <Image src={withMediaUrl(photo)} alt={alternativeText || name} title={alternativeText || name} className="lg:w-11 lg:h-11 w-8 h-8 rounded-full object-cover block" loading="lazy" height={44} width={44} />
                    ) : (
                        <div className="lg:w-11 lg:h-11 w-8 h-8 rounded-full bg-[#080C18] flex items-center justify-center text-white font-bold text-sm">
                            {name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                        </div>
                    )}
                </div>
                <div>
                    <p className="text-white font-semibold text-[15px] m-0">{name}</p>
                    <p className="text-white/70 text-xs m-0">{company}</p>
                </div>
            </div>
        </div>
    </div>
);

const TestimonialsCarousel = ({ data, cardClassName, itemClassName, disableSlider = false }: { data: Testimonial[]; cardClassName?: string; itemClassName?: string; disableSlider?: boolean }) => {
    if (data.length === 0) return null;

    const items = disableSlider ? data : [...data, ...data];
    const containerRef = useRef<HTMLDivElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const xRef = useRef(0);
    const lastTsRef = useRef<number | null>(null);
    const pausedRef = useRef(false);
    const draggingRef = useRef(false);
    const halfWidthRef = useRef(0);
    const dragStartXRef = useRef(0);
    const dragStartTranslateRef = useRef(0);
    const rafRef = useRef<number | null>(null);
    const speedRef = useRef(60);

    // const items = [...testimonialsData, ...testimonialsData];

    const applyTransform = () => {
        if (trackRef.current) {
            if (disableSlider) {
                trackRef.current.style.transform = "none";
                trackRef.current.style.willChange = "auto";
                return;
            }
            trackRef.current.style.transform = `translate3d(${xRef.current}px, 0, 0)`;
        }
    };

    const normalizeX = () => {
        const half = halfWidthRef.current;
        if (half <= 0) return;
        while (xRef.current <= -half) xRef.current += half;
        while (xRef.current > 0) xRef.current -= half;
    };

    useLayoutEffect(() => {
        const track = trackRef.current;
        if (!track) return;

        if (disableSlider) {
            pausedRef.current = true;
            track.style.transform = "none";
            track.style.willChange = "auto";
            return () => {
                if (rafRef.current) cancelAnimationFrame(rafRef.current);
            };
        }

        const measure = () => {
            halfWidthRef.current = track.scrollWidth / 2;
            normalizeX();
            applyTransform();
        };
        measure();

        const ro = new ResizeObserver(measure);
        ro.observe(track);

        const mq = window.matchMedia('(max-width: 767px)');
        const updateSpeed = () => { speedRef.current = mq.matches ? 35 : 60; };
        updateSpeed();
        mq.addEventListener('change', updateSpeed);

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (reduced.matches) pausedRef.current = true;

        const tick = (ts: number) => {
            if (lastTsRef.current == null) lastTsRef.current = ts;
            const dt = (ts - lastTsRef.current) / 1000;
            lastTsRef.current = ts;
            if (!pausedRef.current && !draggingRef.current) {
                xRef.current -= speedRef.current * dt;
                normalizeX();
                applyTransform();
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);

        return () => {
            ro.disconnect();
            mq.removeEventListener('change', updateSpeed);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [disableSlider]);

    const onPointerDown = (e: React.PointerEvent) => {
        if (disableSlider) return;
        draggingRef.current = true;
        dragStartXRef.current = e.clientX;
        dragStartTranslateRef.current = xRef.current;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        lastTsRef.current = null;
    };

    const onPointerMove = (e: React.PointerEvent) => {
        if (disableSlider || !draggingRef.current) return;
        xRef.current = dragStartTranslateRef.current + (e.clientX - dragStartXRef.current);
        normalizeX();
        applyTransform();
    };

    const endDrag = (e: React.PointerEvent) => {
        if (disableSlider || !draggingRef.current) return;
        draggingRef.current = false;
        try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { }
        lastTsRef.current = null;
    };

    return (
        <div
            ref={containerRef}
            className={cn("overflow-hidden select-none", disableSlider ? "cursor-default" : "cursor-grab active:cursor-grabbing")}
            style={{ touchAction: 'pan-y' }}
            onMouseEnter={disableSlider ? undefined : () => { pausedRef.current = true; }}
            onMouseLeave={disableSlider ? undefined : () => { pausedRef.current = false; lastTsRef.current = null; }}
            onPointerDown={disableSlider ? undefined : onPointerDown}
            onPointerMove={disableSlider ? undefined : onPointerMove}
            onPointerUp={disableSlider ? undefined : endDrag}
            onPointerCancel={disableSlider ? undefined : endDrag}
        >
            <div
                ref={trackRef}
                className={cn("flex gap-6", !disableSlider && "will-change-transform")}
                style={{ transform: disableSlider ? 'none' : 'translate3d(0,0,0)' }}
            >
                {items.map((item, i) => (
                    <div
                        key={i}
                        className={cn("flex-shrink-0 w-[calc(100%-30px)] md:w-[calc(50%-12px)] max-w-[500px]", itemClassName)}
                    >
                        <TestimonialCard
                            quote={item.review}
                            name={item.name}
                            company={item.company}
                            photo={item.image?.url}
                            alternativeText={item.image?.alternativeText}
                            className={cardClassName}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default function TestimonialsSection({ data, cardClassName, itemClassName, disableSlider = false }: TestimonialsProps) {
    if (!data) return null;
    return (
        <div className="relative">
            <div className="absolute left-1/2 w-[300px] h-[300px] lg:w-[800px] lg:h-[500px] bg-[#00d4aa]/15 rounded-full blur-3xl pointer-events-none -translate-x-1/2" />
            <div className="relative z-10 w-full ">
                <div className="absolute inset-0 shadow-[20px_0px_70px_100px_#000c1e] z-[11] h-full w-0 left-0 top-0 hidden lg:flex" ></div>
                <div className="absolute  shadow-[20px_0px_70px_150px_#000c1e] z-[11] h-full w-0 right-0 top-0 hidden lg:flex"></div>
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-5 lg:mb-12">
                    <motion.h2 variants={fadeUp} className="section-title">{data.title}{" "} <span className='section-highlight-title'>{data.titleHighlight}</span> </motion.h2>
                </motion.div>

                <div className='flex flex-col gap-3 lg:gap-12'>
                    <TestimonialsCarousel data={data.testimonials || []} cardClassName={cardClassName} itemClassName={itemClassName} disableSlider={disableSlider} />


                    {/* <div className="text-center mt-4">
                        <Link
                            href="/testimonials"
                            className="btn-primary"
                            title="View All Testimonials"
                        >
                            View All Testimonials <ArrowRight className="w-4 h-4 group-hover/cta:translate-x-1 transition-transform" />
                        </Link>
                    </div> */}
                </div>
            </div>
        </div>
    );
}   
